import { Navigate, useLocation } from "react-router-dom";
import { useUser } from "../hooks/useUser";
import { useIsAgent } from "../hooks/useIsAgent";
import { useIsAdmin } from "../hooks/useIsAdmin";
import { AGENT_ROUTES } from "../config/agentPortal";

interface AuthGuardProps {
  children: React.ReactNode;
  noAuth?: boolean;
}

export const AuthGuard: React.FC<AuthGuardProps> = ({
  children,
  noAuth = false,
}) => {
  const { user, loading, error } = useUser();
  const location = useLocation();
  const { isAgent, loading: agentLoading } = useIsAgent();
  const { isAdmin, loading: adminLoading } = useIsAdmin();

  if (loading || agentLoading || adminLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    console.error("AuthGuard: Auth error occurred:", error);
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-600 mb-4">Authentication error occurred</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!user && !noAuth) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  if (user && noAuth) {
    if (isAgent && !isAdmin) {
      return <Navigate to={AGENT_ROUTES.bookings} replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  if (
    user &&
    !noAuth &&
    isAgent &&
    !isAdmin &&
    !location.pathname.startsWith("/agent")
  ) {
    return <Navigate to={AGENT_ROUTES.bookings} replace />;
  }

  return <>{children}</>;
};
